/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import Divider from '@ion/dls/src/components/divider/Divider';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import {CheckmarkFilledIcon} from '@ion/dls/src/icons/CheckmarkFilledIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';

export type NoConflictsFoundAlertProps = {
  conflictsDetectionLoading: boolean;
  readyToSubmit: boolean;
  onBackClick: () => void;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  wrapper : {
    display        : 'flex',
    flexDirection  : 'column',
    alignItems     : 'center',
    justifyContent : 'center',
    color          : dlsColor('grey1'),
    padding        : '26px 30px',
    borderRadius   : '4px !important'
  },
  contentContainer : {
    backgroundColor : dlsColor('white'),
    padding         : '48px',
    margin          : '26px 0px 10px',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    border          : `2px solid ${dlsColor('grey4')}`,
    borderRadius    : 8,
    minHeight       : 250,
    width           : '100%',
    boxSizing       : 'border-box'
  },
  successContent : {
    marginTop     : 10,
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'center'
  },
  iconWrapper : {
    marginBottom : 12
  },
  message : {
    ...dlsAlt('typography', 'label1'),
    textAlign    : 'center',
    fontWeight   : 700,
    fontSize     : 16,
    marginBottom : 32,
    color        : dlsColor('caGreen')
  },
  tipsListItem : {
    ...dlsTypography('body1'),
    padding    : '0px !important',
    lineHeight : '22px'
  },
  updateButton : {
    ...dlsTypography('body1'),
    fontWeight : 500,
    padding    : '0px !important',
    lineHeight : '22px',
    cursor     : 'pointer',
    '&:hover'  : {
      textDecoration : 'underline'
    }
  },
  loader : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px',
    paddingTop : '100px'
  },
  divider : {
    maxWidth       : 200,
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    margin         : '12px 0px'
  },
  or : {
    ...dlsAlt('typography', 'label1'),
    color   : dlsColor('grey3'),
    padding : '0px 8px'
  }
}));

const NoConflictsFoundAlert = ({
  conflictsDetectionLoading,
  readyToSubmit,
  onBackClick
}: NoConflictsFoundAlertProps) => {
  const classes = useStyles();
  const {
    noConflictsFound,
    tip : {
      create,
      review,
      submission
    },
    checking
  } = translate('jobcreate:conflict', {returnObjects: true});

  return (
    <>
      <Render.If condition={conflictsDetectionLoading}>
        <div className={classes.wrapper}>
          <div className={classes.loader}>
            <ButtonSpinner />
            <HeaderTypography variant={'h4'}>
              {checking}
            </HeaderTypography>
          </div>
        </div>
      </Render.If>
      <Render.If condition={readyToSubmit}>
        <div className={classes.wrapper}>
          <div className={classes.contentContainer}>
            <div className={classes.successContent}>
              <div className={classes.iconWrapper}>
                <CheckmarkFilledIcon color={dlsColor('caGreen')} />
              </div>
              <div className={classes.message}>
                {noConflictsFound}
              </div>
              <div>
                <BodyTypography className={classes.tipsListItem}>
                  {create}
                </BodyTypography>
              </div>
              <div className={classes.divider}>
                <Divider/>
                <div className={classes.or}>
                  {translate('common:or')}
                </div>
                <Divider/>
              </div>
              <div>
                <span className={classes.updateButton} onClick={onBackClick}>
                  {review}
                </span>
              &nbsp;
                <span className={classes.tipsListItem}>
                  {submission}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Render.If>
    </>
  );
};

export default NoConflictsFoundAlert;
